import createDOMPurify from 'dompurify';

// A private instance keeps notification rules separate from rich-text surfaces.
const purifier = typeof window === 'undefined' ? null : createDOMPurify(window);
const BLOCK_TAGS = new Set([
  'address', 'article', 'aside', 'blockquote', 'dd', 'div', 'dl', 'dt',
  'figcaption', 'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'header', 'hr', 'li', 'main', 'nav', 'ol', 'p', 'pre', 'section',
  'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'tr', 'ul',
]);
const TEXT_OPTIONS = {
  RETURN_DOM_FRAGMENT: true,
  // Keep head-like tags in the body so their contents can be discarded intact.
  FORCE_BODY: true,
  ALLOWED_TAGS: [
    ...BLOCK_TAGS, 'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'code', 'del',
    'em', 'i', 'ins', 'kbd', 'mark', 'q', 's', 'samp', 'small', 'span',
    'strong', 'sub', 'sup', 'time', 'u', 'wbr',
  ],
  ALLOWED_ATTR: [],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  FORBID_CONTENTS: [
    'annotation-xml', 'audio', 'button', 'canvas', 'datalist', 'desc',
    'embed', 'foreignobject', 'form', 'head', 'iframe', 'math', 'noembed',
    'noframes', 'noscript', 'object', 'picture', 'plaintext', 'script',
    'select', 'style', 'svg', 'template', 'textarea', 'title', 'video', 'xmp',
  ],
};

export function notificationText(value) {
  if (typeof value !== 'string' || !value.trim() || !purifier?.isSupported) {
    return '';
  }

  try {
    const fragment = purifier.sanitize(value, TEXT_OPTIONS);
    if (!(fragment instanceof window.DocumentFragment)) return '';

    const parts = [];
    const visit = (node) => {
      if (node.nodeType === 3) {
        parts.push(node.textContent);
        return;
      }
      const separatesText = node.nodeType === 1 &&
        (BLOCK_TAGS.has(node.localName) || node.localName === 'br');
      if (separatesText) parts.push(' ');
      node.childNodes.forEach(visit);
      if (separatesText) parts.push(' ');
    };
    // Extract once. Reparsing this text would decode literal entities twice.
    visit(fragment);
    return parts.join('').replace(/\s+/g, ' ').trim();
  } catch {
    return '';
  }
}
